// historyText.js — 마케터 1:1 '변경 기록' 한 줄을 사람이 읽는 말로 (2026-10-06 UX 5단계)
//   서버가 쓰는 기록 12종(marketer_consultant_logs.action)을 모두 이름표·대상·바뀐 내용으로 풀어 준다.
//   기록 값(old_values/new_values) 모양은 서버 그대로 — 화면에서 읽기만 한다.
import { toDateString, toDateTimeString } from '../../../utils/date';

export const HISTORY_GROUPS = [
  { key: 'all', label: '전체' },
  { key: 'assign', label: '배정' },
  { key: 'rule', label: '급여 조건' },
  { key: 'decision', label: '넣기·빼기' },
  { key: 'confirm', label: '확정' },
  { key: 'share', label: '상담자배분' },
];

const GROUP_OF = {
  CREATE: 'assign', UPDATE: 'assign', DELETE: 'assign', MOVE: 'assign', END_ASSIGNMENT: 'assign',
  RESIGN_AUTO_END: 'assign', RESIGN_AUTO_END_UNDO: 'assign',
  RULE_CREATE: 'rule', RULE_UPDATE: 'rule', RULE_DELETE: 'rule', RULE_SCHEDULE: 'rule',
  CHARGE_DECISION: 'decision', CONFIRM: 'confirm', REOPEN: 'confirm', SHARE_DECISION: 'share',
};

const TONE = {
  green: { fg: '#15803d', bg: 'rgba(22,163,74,0.12)', bar: '#16a34a' },
  blue: { fg: '#1d4ed8', bg: 'rgba(37,99,235,0.10)', bar: '#2563eb' },
  red: { fg: '#dc2626', bg: 'rgba(220,38,38,0.10)', bar: '#ef4444' },
  purple: { fg: '#7c3aed', bg: 'rgba(124,58,237,0.12)', bar: '#8b5cf6' },
  orange: { fg: '#b45309', bg: 'rgba(245,158,11,0.14)', bar: '#f59e0b' },
  gray: { fg: 'var(--text-sub)', bg: 'rgba(100,116,139,0.14)', bar: '#94a3b8' },
};

function parse(value) {
  if (!value) return {};
  if (typeof value === 'string') {
    try { return JSON.parse(value) || {}; } catch { return {}; }
  }
  return value;
}

const day = (v) => (v ? toDateString(v) : '');
const won = (v) => (v == null || v === '' ? '' : `${Number(v).toLocaleString('ko-KR')}원`);
const yesNo = (on, yes = '함', no = '안 함') => (on ? yes : no);
const SOURCE = { direct: '장수로 직접 계산', incentive_total: '인센티브 관리 금액', none: '계산 안 함' };
const sourceOf = (rule) => rule?.incentive_source || (rule?.incentive_enabled === false ? 'none' : 'direct');

// 급여 조건 칸 — [키, 이름, 값 글자]
const RULE_FIELDS = [
  ['effective_from', '적용 시작일', (r) => day(r.effective_from)],
  ['effective_to', '적용 끝', (r) => day(r.effective_to) || '계속'],
  ['salary_enabled', '월급 계산', (r) => yesNo(r.salary_enabled !== false)],
  ['monthly_salary', '월급', (r) => won(r.monthly_salary)],
  ['incentive_source', '인센티브', (r) => SOURCE[sourceOf(r)]],
  ['incentive_unit_price', '1장당 인센티브', (r) => won(r.incentive_unit_price)],
  ['incentive_base_included_count', '기본 포함 장수', (r) => `${Number(r.incentive_base_included_count || 0).toLocaleString('ko-KR')}장`],
  ['leave_deduction_enabled', '연차 차감', (r) => yesNo(r.leave_deduction_enabled !== false)],
  ['free_leave_days', '무료 연차', (r) => `${Number(r.free_leave_days || 0)}일`],
  ['reorder_excluded', '재계약 제외', (r) => yesNo(r.reorder_excluded !== false)],
  ['cancel_deduction_enabled', '취소 장수 차감', (r) => yesNo(r.cancel_deduction_enabled !== false)],
  ['notes', '메모', (r) => r.notes || '-'],
];

function ruleLines(rule) {
  if (!rule) return [];
  return RULE_FIELDS
    .filter(([key]) => key !== 'effective_to' && key !== 'notes')
    .map(([, label, fmt]) => ({ label, after: fmt(rule) }));
}

function ruleDiff(oldRule = {}, newRule = {}) {
  return RULE_FIELDS
    .map(([, label, fmt]) => ({ label, before: fmt(oldRule), after: fmt(newRule) }))
    .filter((line) => line.before !== line.after);
}

function decisionText(status, eventType) {
  const cancel = eventType === 'cancel';
  if (status === 'included') return cancel ? '차감함' : '넣기';
  if (status === 'excluded') return cancel ? '차감 안 함' : '빼기';
  if (status === 'auto_included') return cancel ? '자동 차감' : '자동 넣기';
  if (status === 'auto_excluded') return cancel ? '자동 차감 안 함' : '자동 빼기';
  return '확인 필요';
}

function people(log, n, o) {
  const asg = n.assignment || o.assignment || o.old_assignment || n.new_assignment || {};
  const consultant = log.consultant_name || n.consultant_name || o.consultant_name || asg.consultant_name || '';
  const marketer = log.marketer_name || n.marketer_name || o.marketer_name || asg.marketer_name || '';
  if (consultant && marketer) return `${consultant} (담당 ${marketer})`;
  return consultant || marketer || '';
}

/** 기록 한 줄 → { group, tone, label, target, lines: [{ label, before?, after }], who, when } */
export function describeLog(log) {
  const n = parse(log.new_values);
  const o = parse(log.old_values);
  const action = String(log.action || '').toUpperCase();
  const base = {
    group: GROUP_OF[action] || 'assign',
    who: log.actor_name || '-',
    when: log.created_at ? toDateTimeString(log.created_at).slice(0, 16) : '',
    target: people(log, n, o),
    lines: [],
  };
  const period = (src) => `${day(src.start_date) || '-'} ~ ${day(src.end_date) || '계속'}`;

  switch (action) {
    case 'CREATE':
      return { ...base, tone: TONE.green, label: '배정 등록', lines: [
        { label: '기간', after: period(n) },
        ...(Number(n.monthly_salary) > 0 ? [{ label: '첫 월급', after: won(n.monthly_salary) }] : []),
        ...(n.notes ? [{ label: '메모', after: n.notes }] : []),
      ] };
    case 'UPDATE':
      return { ...base, tone: TONE.blue, label: '기간·메모 고침', lines: [
        ['start_date', '시작일', day], ['end_date', '끝나는 날', (v) => day(v) || '계속'], ['notes', '메모', (v) => v || '-'],
      ].map(([key, label, fmt]) => ({ label, before: fmt(o[key]), after: fmt(n[key]) })).filter((l) => l.before !== l.after) };
    case 'DELETE':
      return { ...base, tone: TONE.red, label: '배정 지움', lines: [{ label: '지운 배정 기간', after: period(o) }] };
    case 'MOVE':
      return { ...base, tone: TONE.purple, label: '담당 마케터 바꿈', target: n.consultant_name || base.target, lines: [
        { label: '담당 마케터', before: n.old_marketer_name || '-', after: n.new_marketer_name || '-' },
        { label: '새 마케터 시작일', after: day(n.move_start_date) },
        ...(n.old_assignment_end_date ? [{ label: '이전 담당 마지막 날', after: day(n.old_assignment_end_date) }] : []),
      ] };
    case 'END_ASSIGNMENT':
      return { ...base, tone: TONE.gray, label: '배정 끝냄', lines: [{ label: '마지막 근무일', after: day(n.end_date) }] };
    case 'RESIGN_AUTO_END':
      return { ...base, tone: TONE.gray, label: '퇴사로 자동 끝냄', lines: [
        { label: '마지막 근무일', after: day(n.end_date) },
        ...(n.reason ? [{ label: '이유', after: n.reason }] : []),
      ] };
    case 'RESIGN_AUTO_END_UNDO':
      return { ...base, tone: TONE.green, label: '퇴사 취소로 되살림', lines: [{ label: '끝나는 날', after: day(n.end_date) || '계속' }] };
    case 'RULE_CREATE':
      return { ...base, tone: TONE.green, label: '급여 조건 등록', lines: ruleLines(n.billing_rule) };
    case 'RULE_UPDATE':
      return { ...base, tone: TONE.blue, label: '급여 조건 고침', lines: [
        ...ruleDiff(o.billing_rule, n.billing_rule),
        ...(n.correction_reason || o.reason ? [{ label: '고친 이유', after: n.correction_reason || o.reason }] : []),
      ] };
    case 'RULE_DELETE':
      return { ...base, tone: TONE.red, label: '급여 조건 지움', lines: [
        { label: '지운 조건', after: `${day(o.billing_rule?.effective_from) || '-'}부터 · 월급 ${won(o.billing_rule?.monthly_salary) || '-'}` },
      ] };
    case 'RULE_SCHEDULE': {
      const rule = n.billing_rule || {};
      return { ...base, tone: TONE.orange, label: '급여 조건 바꾸기 예약', lines: [
        { label: '새 조건 시작일', after: day(rule.effective_from || n.schedule?.effective_from) },
        ...(n.previous_rule?.effective_to ? [{ label: '이전 조건 마지막 날', after: day(n.previous_rule.effective_to) }] : []),
        ...ruleLines(rule).filter((l) => l.label !== '적용 시작일'),
      ] };
    }
    case 'CHARGE_DECISION':
      return { ...base, tone: TONE.orange, label: n.event_type === 'cancel' ? '취소 차감 정함' : '계약 넣기·빼기', lines: [
        { label: '계약', after: `${day(n.event_date) || '-'} · ${n.company_name || '-'}` },
        { label: '판단', before: decisionText(o.decision_status, n.event_type), after: decisionText(n.decision_status, n.event_type) },
      ] };
    case 'CONFIRM':
      return { ...base, tone: TONE.green, label: `${n.month || '?'}월분 1:1급여 확정`, target: `${n.year || ''}년 ${n.month || ''}월분`, lines: [
        { label: '정산서 날짜', after: day(n.settlement_date) },
        { label: '1:1급여 합계', after: won(n.total_charge_amount) },
        { label: '인원', after: `${Number(n.item_count || 0)}명` },
      ] };
    case 'REOPEN':
      return { ...base, tone: TONE.orange, label: `${n.month || '?'}월분 확정 취소`, target: `${n.year || ''}년 ${n.month || ''}월분`, lines: [
        { label: '취소한 합계', after: won(o.total_charge_amount) },
      ] };
    case 'SHARE_DECISION':
      return { ...base, tone: TONE.purple, label: '상담자배분 판단', target: n.consultant_name || base.target, lines: [
        { label: '계약', after: `${day(n.contract_date) || '-'} · ${n.company_name || '-'}` },
        { label: '판단', before: o.decision_status ? (o.decision_status === 'excluded' ? '빼기' : '넣기') : '자동 넣기', after: n.decision_status === 'excluded' ? '빼기' : '넣기' },
        ...(n.memo ? [{ label: '메모', after: n.memo }] : []),
      ] };
    default:
      return { ...base, tone: TONE.gray, label: '기타 변경' };
  }
}

// 찾기는 대상·누가·이름표에 더해 바뀐 내용(전·후 값)까지 본다 — 예: 마케터 이름으로 담당 바꿈 기록 찾기
export function filterLogs(described, group, keyword) {
  const q = String(keyword || '').trim().toLowerCase();
  const textOf = (item) => [item.target, item.who, item.label, ...item.lines.map((l) => `${l.before || ''} ${l.after || ''}`)]
    .join(' ').toLowerCase();
  return described.filter((item) => (group === 'all' || item.group === group) && (!q || textOf(item).includes(q)));
}
